/**
 * ConfirmationCard — Phase 7E / 7F
 *
 * Renders an action proposal within a chat message bubble, walking the user
 * through the confirmation state machine:
 *
 *   pending    → description + "Shall I proceed?" + Approve / Reject buttons
 *   executing  → spinner (role="status"), buttons disabled
 *   succeeded  → success summary text (green styling)
 *   failed     → error message text (red styling)
 *   rejected   → "Action cancelled" copy, no buttons
 *   expired    → greyed-out card, no buttons, expiry notice
 *
 * Phase 7F additions:
 * - Expiry countdown sourced from proposal.expiresAt (Unix ms timestamp)
 * - Batch detail view for createTasks proposals using proposal.resolvedNames
 * - aria-live="assertive" announcement on expiry
 */

import { useState, useEffect } from "react";
import type { ReactNode } from "react";
import type { ActionProposal, ActionResult, ActionCurrentValues, ActionResultData, ActionResultChange } from "../types/index.js";
import PersonPill from "./PersonPill.js";

// ---------------------------------------------------------------------------
// Date formatting helpers — shared between batch and multi-update detail
// ---------------------------------------------------------------------------

/**
 * Converts an MM-DD-YYYY date string to a human-readable "DD MMM YYYY" label.
 * Also accepts YYYY-MM-DD. Returns the raw input on any parse failure so the
 * user still sees something meaningful rather than "Invalid Date".
 */
function formatDateLabel(input: string): string {
	if (typeof input !== "string" || input.trim().length === 0) return input;
	const parts = input.split("T")[0].split("-");
	if (parts.length < 3) return input;
	const [first, second, third] = parts;
	let year: string;
	let month: string;
	let day: string;
	if (first.length === 4) {
		year = first;
		month = second;
		day = third;
	} else {
		month = first;
		day = second;
		year = third;
	}
	const monthIdx = parseInt(month, 10) - 1;
	const dayNum = parseInt(day, 10);
	const yearNum = parseInt(year, 10);
	if (
		isNaN(monthIdx) ||
		isNaN(dayNum) ||
		isNaN(yearNum) ||
		monthIdx < 0 ||
		monthIdx > 11
	) {
		return input;
	}
	const months = [
		"Jan", "Feb", "Mar", "Apr", "May", "Jun",
		"Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
	];
	return `${dayNum.toString().padStart(2, "0")} ${months[monthIdx]} ${yearNum}`;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ConfirmationCardProps {
	/** The action proposal to display. */
	proposal: ActionProposal;
	/** Current state in the confirmation flow state machine. */
	status: "pending" | "executing" | "succeeded" | "failed" | "rejected";
	/** Result of the action — present in succeeded and failed states. */
	result?: ActionResult;
	/** Called with the actionId when the user approves the action. */
	onApprove: (actionId: string) => void;
	/** Called with the actionId when the user rejects the action. */
	onReject: (actionId: string) => void;
	/**
	 * Error message shown when an approve or reject API call fails.
	 * Set by useChat so the user knows their action did not go through.
	 */
	actionError?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Formats remaining milliseconds as "M:SS". Returns null when ms ≤ 0. */
function formatCountdown(ms: number): string | null {
	if (ms <= 0) return null;
	const totalSeconds = Math.ceil(ms / 1000);
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;
	return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

/**
 * Resolves a ZPUID to a display name using the resolvedNames map.
 * Falls back gracefully to the raw ZPUID if no map is provided.
 */
function resolveName(zpuid: string, resolvedNames?: Record<string, string>): string {
	if (resolvedNames && resolvedNames[zpuid]) {
		return resolvedNames[zpuid];
	}
	return zpuid;
}

// ---------------------------------------------------------------------------
// Task parameter shape for createTasks batch proposals
// ---------------------------------------------------------------------------

interface TaskParam {
	name?: string;
	assigneeIds?: string[];
	priority?: string;
	taskListName?: string;
}

// ---------------------------------------------------------------------------
// SVG icons
// ---------------------------------------------------------------------------

/** Animated spinner shown during the executing state. */
function SpinnerIcon() {
	return (
		<svg
			className="confirmation-card-spinner-svg"
			viewBox="0 0 20 20"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			width="16"
			height="16"
			aria-hidden="true"
		>
			<circle
				cx="10"
				cy="10"
				r="8"
				stroke="currentColor"
				strokeWidth="2"
				strokeDasharray="38"
				strokeDashoffset="10"
				strokeLinecap="round"
			/>
		</svg>
	);
}

/** Tick icon for the succeeded state. */
function TickIcon() {
	return (
		<svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" width="14" height="14" aria-hidden="true">
			<circle cx="7" cy="7" r="6.5" stroke="currentColor" strokeWidth="1.3" />
			<path d="M4 7.2l2 2L10 5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
		</svg>
	);
}

/** Cross icon for the failed state. */
function CrossIcon() {
	return (
		<svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" width="14" height="14" aria-hidden="true">
			<circle cx="7" cy="7" r="6.5" stroke="currentColor" strokeWidth="1.3" />
			<path d="M4.5 4.5l5 5M9.5 4.5l-5 5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
		</svg>
	);
}

/** Dash icon for the rejected state. */
function DashIcon() {
	return (
		<svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" width="14" height="14" aria-hidden="true">
			<circle cx="7" cy="7" r="6.5" stroke="currentColor" strokeWidth="1.3" />
			<path d="M4.5 7h5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
		</svg>
	);
}

/** Clock icon for the expired state. */
function ClockIcon() {
	return (
		<svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" width="14" height="14" aria-hidden="true">
			<circle cx="7" cy="7" r="6.5" stroke="currentColor" strokeWidth="1.3" />
			<path d="M7 4v3.25l2 1.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
		</svg>
	);
}

/** Small external link icon used inside task name links in the result card. */
function ExternalLinkIcon() {
	return (
		<svg
			viewBox="0 0 10 10"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			width="10"
			height="10"
			aria-hidden="true"
			style={{ display: "inline-block", verticalAlign: "middle", marginLeft: 3, flexShrink: 0 }}
		>
			<path
				d="M4 2H2a1 1 0 00-1 1v5a1 1 0 001 1h5a1 1 0 001-1V6M6 1h3m0 0v3m0-3L5 5"
				stroke="currentColor"
				strokeWidth="1.2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

// ---------------------------------------------------------------------------
// ResultDetail sub-component — renders structured success result
// ---------------------------------------------------------------------------

/**
 * Renders a single change row within the ResultDetail card.
 * Shows: Field: <from> → <to> (with PersonPills for user fields).
 */
function ResultChangeRow({ change }: { change: ActionResultChange }) {
	const fromNode = change.from === null ? (
		<span className="confirmation-card-result-detail-none">(none)</span>
	) : change.fromPill ? (
		<PersonPill zpuid={change.fromPill.zpuid} displayName={change.fromPill.displayName} />
	) : (
		<span className="confirmation-card-result-detail-from">{change.from}</span>
	);

	const toNode = change.toPill ? (
		<PersonPill zpuid={change.toPill.zpuid} displayName={change.toPill.displayName} />
	) : (
		<span className="confirmation-card-result-detail-to">{change.to}</span>
	);

	const hasFrom = change.from !== null || change.fromPill;

	return (
		<li className="confirmation-card-result-detail-row">
			<span className="confirmation-card-result-detail-label">{change.field}:</span>{" "}
			{hasFrom ? (
				<>
					{fromNode}{" "}
					<span
						className="confirmation-card-result-detail-arrow"
						aria-label="changed to"
						role="img"
					>
						→
					</span>{" "}
					{toNode}
				</>
			) : (
				toNode
			)}
		</li>
	);
}

/**
 * Structured success result card.
 *
 * Shown in the succeeded state when ActionResult.resultData is present.
 * Renders the entity name (with a deep link for tasks), a muted entity ID,
 * and one row per field that was changed.
 */
function ResultDetail({ resultData }: { resultData: ActionResultData }) {
	const entityLabel = resultData.entityType === "milestone" ? "Milestone" : "Task";

	return (
		<div className="confirmation-card-result-detail">
			<div className="confirmation-card-result-detail-header">
				<span className="confirmation-card-result-detail-entity-label">{entityLabel}:</span>{" "}
				{resultData.entityUrl ? (
					<a
						href={resultData.entityUrl}
						target="_blank"
						rel="noopener noreferrer"
						className="confirmation-card-result-detail-entity-link"
					>
						{resultData.entityName}
						<ExternalLinkIcon />
					</a>
				) : (
					<span className="confirmation-card-result-detail-entity-name">{resultData.entityName}</span>
				)}
				<span className="confirmation-card-result-detail-entity-id">{resultData.entityId}</span>
			</div>
			{resultData.changes.length > 0 && (
				<ul className="confirmation-card-result-detail-list">
					{resultData.changes.map((change, idx) => (
						<ResultChangeRow key={`${change.field}-${idx}`} change={change} />
					))}
				</ul>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Subtask parameter shape for createSubtasks batch proposals
// ---------------------------------------------------------------------------

interface SubtaskParam {
	name?: string;
	assigneeIds?: string[];
	priority?: string;
}

// ---------------------------------------------------------------------------
// BatchDetail sub-component
// ---------------------------------------------------------------------------

/**
 * Structured list view for createTasks proposals.
 * Replaces the plain description text with a numbered list showing each
 * task's name, assignees, priority, and task list.
 */
function BatchDetail({
	tasks,
	resolvedNames,
}: {
	tasks: TaskParam[];
	resolvedNames?: Record<string, string>;
}) {
	return (
		<div className="confirmation-card-batch-detail">
			<p className="confirmation-card-batch-detail-header">
				PIA wants to create {tasks.length} task{tasks.length !== 1 ? "s" : ""}:
			</p>
			<ol className="confirmation-card-batch-detail-list">
				{tasks.map((task, idx) => {
					const assigneeNames = task.assigneeIds && task.assigneeIds.length > 0
						? task.assigneeIds.map((id) => resolveName(id, resolvedNames))
						: null;

					const hasAssignees = assigneeNames && assigneeNames.length > 0;
					const hasPriority = Boolean(task.priority);
					const hasTaskList = Boolean(task.taskListName);
					const hasMeta = hasAssignees || hasPriority || hasTaskList;

					return (
						<li key={idx} className="confirmation-card-batch-detail-item">
							<span className="confirmation-card-batch-detail-name">
								{task.name ?? `Task ${idx + 1}`}
							</span>
							{hasMeta && (
								<span className="confirmation-card-batch-detail-meta">
									{hasAssignees && (
										<span className="confirmation-card-batch-detail-meta-item">
											<span className="confirmation-card-batch-detail-meta-label">
												{assigneeNames!.length === 1 ? "Assignee" : "Assignees"}:
											</span>{" "}
											{assigneeNames!.join(", ")}
										</span>
									)}
									{hasPriority && (
										<span className="confirmation-card-batch-detail-meta-item">
											<span className="confirmation-card-batch-detail-meta-label">Priority:</span>{" "}
											{task.priority}
										</span>
									)}
									{hasTaskList && (
										<span className="confirmation-card-batch-detail-meta-item">
											<span className="confirmation-card-batch-detail-meta-label">Task list:</span>{" "}
											{task.taskListName}
										</span>
									)}
								</span>
							)}
						</li>
					);
				})}
			</ol>
		</div>
	);
}

// ---------------------------------------------------------------------------
// CreateSubtasksDetail sub-component — renders createSubtasks proposals
// ---------------------------------------------------------------------------

/**
 * Structured list view for createSubtasks proposals.
 * Shows the parent task name (if available via resolvedNames) and a numbered
 * list of subtasks to be created.
 */
function CreateSubtasksDetail({
	parentTaskId,
	subtasks,
	resolvedNames,
}: {
	parentTaskId?: string;
	subtasks: SubtaskParam[];
	resolvedNames?: Record<string, string>;
}) {
	const parentName = parentTaskId
		? (resolvedNames?.[parentTaskId] ?? parentTaskId)
		: null;

	return (
		<div className="confirmation-card-batch-detail">
			{parentName && (
				<p className="confirmation-card-batch-detail-header">
					PIA wants to add {subtasks.length} subtask{subtasks.length !== 1 ? "s" : ""} to '{parentName}'
				</p>
			)}
			{!parentName && (
				<p className="confirmation-card-batch-detail-header">
					PIA wants to create {subtasks.length} subtask{subtasks.length !== 1 ? "s" : ""}:
				</p>
			)}
			<ol className="confirmation-card-batch-detail-list">
				{subtasks.map((subtask, idx) => {
					const assigneeNames = subtask.assigneeIds && subtask.assigneeIds.length > 0
						? subtask.assigneeIds.map((id) => resolveName(id, resolvedNames))
						: null;
					const hasAssignees = assigneeNames && assigneeNames.length > 0;
					const hasPriority = Boolean(subtask.priority);
					const hasMeta = hasAssignees || hasPriority;

					return (
						<li key={idx} className="confirmation-card-batch-detail-item">
							<span className="confirmation-card-batch-detail-name">
								{subtask.name ?? `Subtask ${idx + 1}`}
							</span>
							{hasMeta && (
								<span className="confirmation-card-batch-detail-meta">
									{hasAssignees && (
										<span className="confirmation-card-batch-detail-meta-item">
											<span className="confirmation-card-batch-detail-meta-label">
												{assigneeNames!.length === 1 ? "Assignee" : "Assignees"}:
											</span>{" "}
											{assigneeNames!.join(", ")}
										</span>
									)}
									{hasPriority && (
										<span className="confirmation-card-batch-detail-meta-item">
											<span className="confirmation-card-batch-detail-meta-label">Priority:</span>{" "}
											{subtask.priority}
										</span>
									)}
								</span>
							)}
						</li>
					);
				})}
			</ol>
		</div>
	);
}

// ---------------------------------------------------------------------------
// SingleUpdateDetail sub-component — renders single-field update proposals
// ---------------------------------------------------------------------------

/**
 * A single diff row used by SingleUpdateDetail.
 * Renders: Label: <current> → <proposed>
 * Falls back gracefully when currentText is not available.
 */
function SingleDiffRow({
	label,
	proposedNode,
	proposedText,
	currentText,
	currentNode,
	hasDiff,
}: {
	label: string;
	proposedNode: ReactNode;
	proposedText: string;
	currentText: string | null | undefined;
	currentNode: ReactNode;
	hasDiff: boolean;
}) {
	// No diff available — render plain proposed value
	if (!hasDiff || currentText === undefined) {
		return (
			<li className="confirmation-card-multi-detail-row">
				<span className="confirmation-card-multi-detail-label">{label}</span>{" "}
				<span className="confirmation-card-multi-detail-value">{proposedNode}</span>
			</li>
		);
	}

	// Unchanged — same current and proposed
	const isUnchanged =
		currentText !== null &&
		currentText.trim().toLowerCase() === proposedText.trim().toLowerCase();

	if (isUnchanged) {
		return (
			<li className="confirmation-card-multi-detail-row confirmation-card-diff-row confirmation-card-diff-row--unchanged">
				<span className="confirmation-card-multi-detail-label">{label}</span>{" "}
				<span className="confirmation-card-multi-detail-value">{proposedNode}</span>{" "}
				<span className="confirmation-card-diff-unchanged-note">(unchanged)</span>
			</li>
		);
	}

	const currentRender: ReactNode =
		currentText === null ? (
			<span className="confirmation-card-diff-none">(none)</span>
		) : (
			currentNode
		);

	return (
		<li className="confirmation-card-multi-detail-row confirmation-card-diff-row">
			<span className="confirmation-card-multi-detail-label">{label}</span>{" "}
			<span className="confirmation-card-diff-current">{currentRender}</span>{" "}
			<span
				className="confirmation-card-diff-arrow"
				aria-label="changes to"
				role="img"
			>
				→
			</span>{" "}
			<span className="confirmation-card-diff-proposed">{proposedNode}</span>
		</li>
	);
}

/** Resolved task name for display in single-update headers. */
function resolveTaskName(
	taskId: string | undefined,
	resolvedNames?: Record<string, string>,
): string {
	if (!taskId) return "this task";
	return resolvedNames?.[taskId] ?? taskId;
}

/**
 * Detail view for single-field update proposals.
 *
 * Handles: assignTask, updateTaskStatus, updateTaskPriority, updateTaskDates,
 * updateTaskCompletion, updateTaskClientContact, updateMilestoneStatus.
 *
 * Shows a task/milestone name header and a compact list of the field(s)
 * being changed. Renders before→after diffs when currentValues is present.
 */
function SingleUpdateDetail({
	functionName,
	params,
	resolvedNames,
	currentValues,
}: {
	functionName: string;
	params: Record<string, unknown>;
	resolvedNames?: Record<string, string>;
	currentValues?: ActionCurrentValues;
}) {
	const hasDiff = currentValues !== undefined;

	// Determine the target entity name and header verb
	const taskId = typeof params.taskId === "string" ? params.taskId : undefined;
	const milestoneId = typeof params.milestoneId === "string" ? params.milestoneId : undefined;
	const targetId = taskId ?? milestoneId;
	const isMilestone = functionName === "updateMilestoneStatus";
	const fallbackName = isMilestone ? "this milestone" : "this task";
	const targetName = targetId
		? (resolvedNames?.[targetId] ?? targetId)
		: fallbackName;
	const headerVerb = isMilestone ? "milestone" : "task";
	const headerText = `PIA wants to update ${headerVerb} '${targetName}'`;

	// Build rows based on function type
	type DiffRowData = {
		key: string;
		label: string;
		proposedText: string;
		proposedNode: ReactNode;
		currentText: string | null | undefined;
		currentNode: ReactNode;
	};
	const rows: DiffRowData[] = [];

	if (functionName === "assignTask") {
		const userIds = Array.isArray(params.userIds) ? (params.userIds as string[]) : [];
		const proposedName = userIds.length > 0
			? userIds.map((id) => resolveName(id, resolvedNames)).join(", ")
			: "(unknown)";
		const currentOwner = currentValues?.owner;
		const currentText = currentOwner?.displayName ?? null;

		rows.push({
			key: "assignee",
			label: "Assign to:",
			proposedText: proposedName,
			proposedNode: proposedName,
			currentText: hasDiff ? currentText : undefined,
			currentNode: currentText,
		});
	}

	if (functionName === "updateTaskStatus") {
		const proposedStatus = typeof params.statusName === "string" ? params.statusName : String(params.statusName ?? "");
		const currentText = hasDiff
			? currentValueToText(currentValues?.status ?? null, false)
			: undefined;
		rows.push({
			key: "status",
			label: "Status:",
			proposedText: proposedStatus,
			proposedNode: proposedStatus,
			currentText,
			currentNode: currentText,
		});
	}

	if (functionName === "updateMilestoneStatus") {
		const completed = Boolean(params.completed);
		const proposedText = completed ? "Yes" : "No";
		const currentPct = currentValues?.completionPercentage;
		const currentText = hasDiff
			? (currentPct !== undefined ? (currentPct >= 100 ? "Yes" : `${currentPct}% complete`) : null)
			: undefined;
		rows.push({
			key: "completed",
			label: "Completed:",
			proposedText,
			proposedNode: proposedText,
			currentText,
			currentNode: currentText,
		});
	}

	if (functionName === "updateTaskPriority") {
		const proposedPriority = typeof params.priority === "string" ? params.priority : String(params.priority ?? "");
		const currentText = hasDiff
			? currentValueToText(currentValues?.priority ?? null, false)
			: undefined;
		rows.push({
			key: "priority",
			label: "Priority:",
			proposedText: proposedPriority,
			proposedNode: proposedPriority,
			currentText,
			currentNode: currentText,
		});
	}

	if (functionName === "updateTaskDates") {
		if (params.startDate !== undefined) {
			const raw = String(params.startDate);
			const proposedFormatted = formatDateLabel(raw);
			const currentText = hasDiff
				? currentValueToText(currentValues?.startDate ?? null, true)
				: undefined;
			rows.push({
				key: "startDate",
				label: "Start date:",
				proposedText: proposedFormatted,
				proposedNode: proposedFormatted,
				currentText,
				currentNode: currentText,
			});
		}
		if (params.endDate !== undefined) {
			const raw = String(params.endDate);
			const proposedFormatted = formatDateLabel(raw);
			const currentText = hasDiff
				? currentValueToText(currentValues?.endDate ?? null, true)
				: undefined;
			rows.push({
				key: "endDate",
				label: "End date:",
				proposedText: proposedFormatted,
				proposedNode: proposedFormatted,
				currentText,
				currentNode: currentText,
			});
		}
	}

	if (functionName === "updateTaskCompletion") {
		const completed = params.completed === true || params.completed === "true";
		const proposedText = completed ? "Yes" : "No";
		const currentPct = currentValues?.completionPercentage;
		const currentCompletedText = hasDiff
			? (currentPct !== undefined ? (currentPct >= 100 ? "Yes" : `${currentPct}% complete`) : null)
			: undefined;
		rows.push({
			key: "completed",
			label: "Completed:",
			proposedText,
			proposedNode: proposedText,
			currentText: currentCompletedText,
			currentNode: currentCompletedText,
		});
	}

	if (functionName === "updateTaskClientContact") {
		const proposed = typeof params.clientContact === "string" ? params.clientContact : String(params.clientContact ?? "");
		const currentText = hasDiff
			? currentValueToText(currentValues?.clientContact ?? null, false)
			: undefined;
		rows.push({
			key: "clientContact",
			label: "Client contact:",
			proposedText: proposed,
			proposedNode: proposed,
			currentText,
			currentNode: currentText,
		});
	}

	// Fallback for unknown single-update types — show the raw description line
	if (rows.length === 0) {
		return (
			<div className="confirmation-card-multi-detail">
				<p className="confirmation-card-multi-detail-header">{headerText}</p>
			</div>
		);
	}

	return (
		<div className="confirmation-card-multi-detail">
			<p className="confirmation-card-multi-detail-header">{headerText}</p>
			<ul className="confirmation-card-multi-detail-list">
				{rows.map((row) => (
					<SingleDiffRow
						key={row.key}
						label={row.label}
						proposedNode={row.proposedNode}
						proposedText={row.proposedText}
						currentText={row.currentText}
						currentNode={row.currentNode}
						hasDiff={hasDiff}
					/>
				))}
			</ul>
		</div>
	);
}

// ---------------------------------------------------------------------------
// CommentDetail sub-component — renders addTaskComment proposals
// ---------------------------------------------------------------------------

/** Truncation threshold for comment body preview. */
const COMMENT_TRUNCATE_AT = 200;

/**
 * Detail view for addTaskComment proposals.
 * Shows the target task name and the comment text to be posted.
 * There is no "before" state — comments are additive.
 */
function CommentDetail({
	params,
	resolvedNames,
}: {
	params: Record<string, unknown>;
	resolvedNames?: Record<string, string>;
}) {
	const [expanded, setExpanded] = useState(false);
	const taskId = typeof params.taskId === "string" ? params.taskId : undefined;
	const taskName = resolveTaskName(taskId, resolvedNames);
	const comment = typeof params.content === "string" ? params.content : "";

	const isLong = comment.length > COMMENT_TRUNCATE_AT;
	const shown = expanded || !isLong
		? comment
		: `${comment.slice(0, COMMENT_TRUNCATE_AT)}…`;

	return (
		<div className="confirmation-card-multi-detail">
			<p className="confirmation-card-multi-detail-header">
				PIA wants to add a comment to '{taskName}'
			</p>
			<ul className="confirmation-card-multi-detail-list">
				<li className="confirmation-card-multi-detail-row">
					<span className="confirmation-card-multi-detail-label">Comment:</span>{" "}
					<span className="confirmation-card-multi-detail-value">
						<span className="confirmation-card-multi-detail-value-text">{shown}</span>
						{isLong && (
							<button
								type="button"
								className="confirmation-card-multi-detail-toggle"
								onClick={() => setExpanded((v) => !v)}
								aria-expanded={expanded}
							>
								{expanded ? "Show less" : "Show more"}
							</button>
						)}
					</span>
				</li>
			</ul>
		</div>
	);
}

// ---------------------------------------------------------------------------
// MultiUpdateDetail sub-component — renders updateTaskMulti proposals
// ---------------------------------------------------------------------------

/** Parameter shape for an updateTaskMulti proposal. */
interface MultiUpdateParam {
	taskId?: string;
	description?: string;
	priority?: string;
	startDate?: string;
	endDate?: string;
	statusName?: string;
	assigneeIds?: string[];
	completed?: boolean;
	milestoneId?: string;
	milestoneName?: string;
	clientContact?: string;
}

/** Threshold above which the description is truncated with a "Show more" toggle. */
const DESCRIPTION_TRUNCATE_AT = 120;

/**
 * Normalise any of the ActionCurrentValues "current" values to a renderable
 * string for the diff. Returns null when the value represents "no current
 * value" (empty string or null input) so the caller can emit a "(none)"
 * placeholder; returns the formatted string otherwise.
 *
 * Dates are formatted as DD MMM YYYY via formatDateLabel.
 */
function currentValueToText(
	value: string | number | null | undefined,
	formatAsDate: boolean,
): string | null {
	if (value === null || value === undefined) return null;
	if (typeof value === "string") {
		if (value.length === 0) return null;
		return formatAsDate ? formatDateLabel(value) : value;
	}
	return String(value);
}

/**
 * Structured detail view for an updateTaskMulti proposal.
 *
 * Shows a header naming the task and a list of only the fields that will
 * change. When `currentValues` is provided, each row renders as a diff:
 *   <current> → <proposed>
 * with the arrow carrying aria-label="changes to". When current equals
 * proposed (after normalisation), the row shows a dimmed "unchanged" note
 * and the arrow is omitted.
 *
 * The description field is flagged as "replaces existing" to warn the user
 * that approval wipes the previous description. Long descriptions collapse
 * behind a "Show more" toggle.
 */
function MultiUpdateDetail({
	params,
	taskName,
	resolvedNames,
	currentValues,
}: {
	params: MultiUpdateParam;
	taskName: string;
	resolvedNames?: Record<string, string>;
	currentValues?: ActionCurrentValues;
}) {
	const [descExpanded, setDescExpanded] = useState(false);
	const hasDiff = currentValues !== undefined;

	type Row = {
		key: string;
		label: string;
		labelClass?: string;
		/** Raw proposed value text, pre-rendering, used for unchanged-detection. */
		proposedText?: string;
		/** Rendered proposed ReactNode. */
		proposedNode: ReactNode;
		/** Rendered current ReactNode (only consulted when hasDiff). */
		currentNode?: ReactNode;
		/** Raw current text — null means "(none)". */
		currentText?: string | null;
	};

	const rows: Row[] = [];

	// Description (replaces existing) — with truncation
	if (params.description !== undefined) {
		const isLong = params.description.length > DESCRIPTION_TRUNCATE_AT;
		const shown = descExpanded || !isLong
			? params.description
			: `${params.description.slice(0, DESCRIPTION_TRUNCATE_AT)}…`;
		rows.push({
			key: "description",
			label: "Description (replaces existing):",
			labelClass: "confirmation-card-multi-detail-label--destructive",
			proposedText: params.description,
			proposedNode: (
				<>
					<span className="confirmation-card-multi-detail-value-text">{shown}</span>
					{isLong && (
						<button
							type="button"
							className="confirmation-card-multi-detail-toggle"
							onClick={() => setDescExpanded((v) => !v)}
							aria-expanded={descExpanded}
						>
							{descExpanded ? "Show less" : "Show more"}
						</button>
					)}
				</>
			),
			currentText: currentValueToText(currentValues?.description ?? null, false),
			currentNode: currentValueToText(currentValues?.description ?? null, false),
		});
	}

	if (params.priority !== undefined) {
		const current = currentValueToText(currentValues?.priority ?? null, false);
		rows.push({
			key: "priority",
			label: "Priority:",
			proposedText: params.priority,
			proposedNode: params.priority,
			currentText: current,
			currentNode: current,
		});
	}

	if (params.startDate !== undefined) {
		const proposedFormatted = formatDateLabel(params.startDate);
		const current = currentValueToText(currentValues?.startDate ?? null, true);
		rows.push({
			key: "startDate",
			label: "Start date:",
			proposedText: proposedFormatted,
			proposedNode: proposedFormatted,
			currentText: current,
			currentNode: current,
		});
	}

	if (params.endDate !== undefined) {
		const proposedFormatted = formatDateLabel(params.endDate);
		const current = currentValueToText(currentValues?.endDate ?? null, true);
		rows.push({
			key: "endDate",
			label: "End date:",
			proposedText: proposedFormatted,
			proposedNode: proposedFormatted,
			currentText: current,
			currentNode: current,
		});
	}

	if (params.statusName !== undefined) {
		const current = currentValueToText(currentValues?.status ?? null, false);
		rows.push({
			key: "status",
			label: "Status:",
			proposedText: params.statusName,
			proposedNode: params.statusName,
			currentText: current,
			currentNode: current,
		});
	}

	if (params.assigneeIds !== undefined && params.assigneeIds.length > 0) {
		const names = params.assigneeIds.map((id) => resolveName(id, resolvedNames)).join(", ");
		// For assignees, current comes from the owner snapshot (displayName is the
		// comma-joined current assignee list when multiple exist).
		const currentOwner = currentValues?.owner;
		const currentAssignees = currentOwner?.displayName ?? null;
		const current = currentValueToText(currentAssignees, false);
		rows.push({
			key: "assignees",
			label: "Assignees:",
			proposedText: names,
			proposedNode: names,
			currentText: current,
			currentNode: current,
		});
	}

	if (params.completed !== undefined) {
		const proposedText = params.completed ? "Yes" : "No";
		rows.push({
			key: "completed",
			label: "Completed:",
			proposedText,
			proposedNode: proposedText,
			currentText: undefined,
			currentNode: undefined,
		});
	}

	if (params.milestoneName !== undefined || params.milestoneId !== undefined) {
		const proposedText = params.milestoneName ?? params.milestoneId!;
		const current = currentValueToText(currentValues?.milestoneName ?? null, false);
		rows.push({
			key: "milestone",
			label: "Milestone:",
			proposedText,
			proposedNode: proposedText,
			currentText: current,
			currentNode: current,
		});
	}

	if (params.clientContact !== undefined) {
		const current = currentValueToText(currentValues?.clientContact ?? null, false);
		rows.push({
			key: "clientContact",
			label: "Client contact:",
			proposedText: params.clientContact,
			proposedNode: params.clientContact,
			currentText: current,
			currentNode: current,
		});
	}

	// ---- Render ----

	return (
		<div className="confirmation-card-multi-detail">
			<p className="confirmation-card-multi-detail-header">
				PIA wants to update task '{taskName}'
			</p>
			<ul className="confirmation-card-multi-detail-list">
				{rows.map((row) => {
					const labelClass = `confirmation-card-multi-detail-label${
						row.labelClass ? ` ${row.labelClass}` : ""
					}`;
					// Only the description/completed rows have no "current" equivalent.
					// When hasDiff is true and this row has a current value available,
					// render the diff layout.
					const hasCurrent = hasDiff && row.currentText !== undefined;

					if (!hasCurrent) {
						// Legacy / back-compat single-column layout.
						return (
							<li key={row.key} className="confirmation-card-multi-detail-row">
								<span className={labelClass}>{row.label}</span>{" "}
								<span className="confirmation-card-multi-detail-value">{row.proposedNode}</span>
							</li>
						);
					}

					// Diff row — compare normalised current + proposed text to detect
					// "no change". When equal (case-insensitive, trimmed), render a
					// dimmed row with an "unchanged" note and omit the arrow.
					const isUnchanged =
						row.currentText !== null &&
						row.currentText !== undefined &&
						row.proposedText !== undefined &&
						row.currentText.trim().toLowerCase() === row.proposedText.trim().toLowerCase();

					if (isUnchanged) {
						return (
							<li key={row.key} className="confirmation-card-multi-detail-row confirmation-card-diff-row confirmation-card-diff-row--unchanged">
								<span className={labelClass}>{row.label}</span>{" "}
								<span className="confirmation-card-multi-detail-value">{row.proposedNode}</span>{" "}
								<span className="confirmation-card-diff-unchanged-note">(unchanged)</span>
							</li>
						);
					}

					const currentRender: ReactNode =
						row.currentText === null ? (
							<span className="confirmation-card-diff-none">(none)</span>
						) : (
							row.currentNode
						);

					return (
						<li key={row.key} className="confirmation-card-multi-detail-row confirmation-card-diff-row">
							<span className={labelClass}>{row.label}</span>{" "}
							<span className="confirmation-card-diff-current">{currentRender}</span>{" "}
							<span
								className="confirmation-card-diff-arrow"
								aria-label="changes to"
								role="img"
							>
								→
							</span>{" "}
							<span className="confirmation-card-diff-proposed">{row.proposedNode}</span>
						</li>
					);
				})}
			</ul>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Module-level constants
// ---------------------------------------------------------------------------

/**
 * Set of functionName values that are routed to SingleUpdateDetail.
 * Defined at module scope so it is not re-created on every render.
 */
const SINGLE_UPDATE_TYPES = new Set([
	"assignTask",
	"updateTaskStatus",
	"updateTaskPriority",
	"updateTaskDates",
	"updateTaskCompletion",
	"updateTaskClientContact",
	"updateMilestoneStatus",
]);

// ---------------------------------------------------------------------------
// ConfirmationCard component
// ---------------------------------------------------------------------------

/**
 * Confirmation card for proposed write-back actions.
 *
 * Exported as default so the test import resolves:
 *   import ConfirmationCard from "../../components/ConfirmationCard.js";
 */
export default function ConfirmationCard({
	proposal,
	status,
	result,
	onApprove,
	onReject,
	actionError,
}: ConfirmationCardProps) {
	// -----------------------------------------------------------------------
	// Expiry countdown (Phase 7F)
	// -----------------------------------------------------------------------

	const [remainingMs, setRemainingMs] = useState<number | null>(() => {
		if (!proposal.expiresAt) return null;
		return Math.max(0, proposal.expiresAt - Date.now());
	});

	// Track whether this card has auto-transitioned to expired.
	const [isExpired, setIsExpired] = useState(false);

	useEffect(() => {
		if (!proposal.expiresAt) return;
		// Don't start interval if already past expiry on mount.
		if (Date.now() >= proposal.expiresAt) {
			setIsExpired(true);
			setRemainingMs(0);
			return;
		}

		const intervalId = setInterval(() => {
			const remaining = proposal.expiresAt! - Date.now();
			if (remaining <= 0) {
				setRemainingMs(0);
				setIsExpired(true);
				clearInterval(intervalId);
			} else {
				setRemainingMs(remaining);
			}
		}, 1000);

		return () => clearInterval(intervalId);
	}, [proposal.expiresAt]);

	// -----------------------------------------------------------------------
	// Derived state
	// -----------------------------------------------------------------------

	const isExecuting = status === "executing";
	// Buttons are disabled if executing, or if the proposal has expired.
	const buttonsDisabled = isExecuting || isExpired;
	const showButtons = (status === "pending" || status === "executing") && !isExpired;

	// Determine which detail component to render.
	const fn = proposal.functionName;

	// createTasks batch
	const isBatchCreate =
		fn === "createTasks" &&
		Array.isArray(proposal.parameters.tasks);
	const batchTasks = isBatchCreate
		? (proposal.parameters.tasks as TaskParam[])
		: null;

	// createSubtasks batch
	const isSubtaskCreate =
		fn === "createSubtasks" &&
		Array.isArray(proposal.parameters.subtasks);
	const subtaskList = isSubtaskCreate
		? (proposal.parameters.subtasks as SubtaskParam[])
		: null;
	const subtaskParentId = typeof proposal.parameters.parentTaskId === "string"
		? proposal.parameters.parentTaskId
		: undefined;

	// updateTaskMulti (existing detail component)
	const isMultiUpdate = fn === "updateTaskMulti";
	const multiParams = isMultiUpdate
		? (proposal.parameters as MultiUpdateParam)
		: null;
	// Defensive guard — fall back to the plain description if params are malformed.
	const multiParamsValid =
		isMultiUpdate &&
		multiParams !== null &&
		typeof multiParams === "object" &&
		typeof multiParams.taskId === "string" &&
		multiParams.taskId.length > 0;
	const multiTaskName = multiParamsValid
		? (proposal.resolvedNames?.[multiParams!.taskId!] ?? multiParams!.taskId!)
		: "";

	// Single-field update actions — routed to SingleUpdateDetail
	const isSingleUpdate = SINGLE_UPDATE_TYPES.has(fn);

	// addTaskComment — routed to CommentDetail
	const isComment = fn === "addTaskComment";

	// Build the countdown label for the footer.
	const countdownLabel = remainingMs !== null ? formatCountdown(remainingMs) : null;

	// -----------------------------------------------------------------------
	// Render
	// -----------------------------------------------------------------------

	return (
		<div
			className={`confirmation-card confirmation-card--${status}${isExpired ? " confirmation-card--expired" : ""}`}
			aria-live="polite"
			aria-atomic="true"
		>
			{/* Pending / executing — show description (or batch / multi-update detail) and action buttons */}
			{(status === "pending" || status === "executing") && (
				<>
					{isBatchCreate && batchTasks ? (
						<BatchDetail tasks={batchTasks} resolvedNames={proposal.resolvedNames} />
					) : isSubtaskCreate && subtaskList ? (
						<CreateSubtasksDetail
							parentTaskId={subtaskParentId}
							subtasks={subtaskList}
							resolvedNames={proposal.resolvedNames}
						/>
					) : isComment ? (
						<CommentDetail
							params={proposal.parameters}
							resolvedNames={proposal.resolvedNames}
						/>
					) : isSingleUpdate ? (
						<SingleUpdateDetail
							functionName={fn}
							params={proposal.parameters}
							resolvedNames={proposal.resolvedNames}
							currentValues={proposal.currentValues}
						/>
					) : multiParamsValid ? (
						<MultiUpdateDetail
							params={multiParams!}
							taskName={multiTaskName}
							resolvedNames={proposal.resolvedNames}
							currentValues={proposal.currentValues}
						/>
					) : (
						<div className="confirmation-card-description">
							<span className="confirmation-card-description-text">{proposal.description}</span>
							{status === "pending" && !isExpired && (
								<span className="confirmation-card-prompt"> Shall I proceed?</span>
							)}
						</div>
					)}

					{isExecuting && (
						<div className="confirmation-card-executing" role="status" aria-label="Executing action">
							<SpinnerIcon />
							<span className="confirmation-card-executing-text">Executing…</span>
						</div>
					)}

					{/* Expired notice (inline, within the pending branch) */}
					{isExpired && (
						<div className="confirmation-card-expired-notice">
							<ClockIcon />
							<span>This action has expired.</span>
						</div>
					)}

					{showButtons && (
						<div className="confirmation-card-actions">
							<button
								className="confirmation-card-btn confirmation-card-btn--approve"
								onClick={() => onApprove(proposal.actionId)}
								disabled={buttonsDisabled}
								aria-label={(isBatchCreate || isSubtaskCreate) ? "Approve All" : "Approve"}
							>
								{(isBatchCreate || isSubtaskCreate) ? "Approve All" : "Approve"}
							</button>
							<button
								className="confirmation-card-btn confirmation-card-btn--reject"
								onClick={() => onReject(proposal.actionId)}
								disabled={buttonsDisabled}
								aria-label="Reject"
							>
								Reject
							</button>
						</div>
					)}

					{/* Action error — shown when an approve or reject API call failed */}
					{actionError && (
						<div className="confirmation-card-action-error" role="alert">
							{actionError}
						</div>
					)}

					{/* Footer: countdown timer — aria-live="off" to avoid announcing every tick */}
					{countdownLabel && !isExpired && (
						<div className="confirmation-card-footer">
							<span
								className={`confirmation-card-countdown${
									remainingMs !== null && remainingMs < 30_000
										? " confirmation-card-countdown--urgent"
										: ""
								}`}
								aria-live="off"
							>
								Expires in {countdownLabel}
							</span>
						</div>
					)}
				</>
			)}

			{/* Succeeded state */}
			{status === "succeeded" && (
				<div className="confirmation-card-result confirmation-card-result--success">
					<div className="confirmation-card-result-header">
						<TickIcon />
						<span className="confirmation-card-result-title">
							{result?.resultData?.entityType === "comment" ? "Comment added" : "Updated successfully"}
						</span>
					</div>
					{result?.resultData ? (
						<ResultDetail resultData={result.resultData} />
					) : (
						<span className="confirmation-card-result-text">
							{result?.summary ?? "Action completed successfully."}
						</span>
					)}
					{result?.results && result.results.length > 0 && (
						<div className="confirmation-card-batch-results">
							{result.results.map((taskResult, idx) => (
								<div
									key={`${taskResult.name}-${idx}`}
									className={`confirmation-card-batch-item confirmation-card-batch-item--${taskResult.success ? "success" : "error"}`}
								>
									{taskResult.success ? <TickIcon /> : <CrossIcon />}
									<span className="confirmation-card-batch-item-name">{taskResult.name}</span>
									{taskResult.success && taskResult.taskId && (
										<span className="confirmation-card-batch-item-id">(ID: {taskResult.taskId})</span>
									)}
									{!taskResult.success && taskResult.error && (
										<span className="confirmation-card-batch-item-error">{taskResult.error}</span>
									)}
								</div>
							))}
							<div className="confirmation-card-batch-summary">
								Created {result.results.filter((r) => r.success).length} of {result.results.length} tasks successfully
							</div>
						</div>
					)}
				</div>
			)}

			{/* Failed state */}
			{status === "failed" && (
				<div className="confirmation-card-result confirmation-card-result--error">
					<CrossIcon />
					<span className="confirmation-card-result-text">
						{result?.error ?? "Action failed."}
					</span>
					{result?.results && result.results.length > 0 && (
						<div className="confirmation-card-batch-results">
							{result.results.map((taskResult, idx) => (
								<div
									key={`${taskResult.name}-${idx}`}
									className={`confirmation-card-batch-item confirmation-card-batch-item--${taskResult.success ? "success" : "error"}`}
								>
									{taskResult.success ? <TickIcon /> : <CrossIcon />}
									<span className="confirmation-card-batch-item-name">{taskResult.name}</span>
									{taskResult.success && taskResult.taskId && (
										<span className="confirmation-card-batch-item-id">(ID: {taskResult.taskId})</span>
									)}
									{!taskResult.success && taskResult.error && (
										<span className="confirmation-card-batch-item-error">{taskResult.error}</span>
									)}
								</div>
							))}
							<div className="confirmation-card-batch-summary">
								Created {result.results.filter((r) => r.success).length} of {result.results.length} tasks successfully
							</div>
						</div>
					)}
				</div>
			)}

			{/* Rejected state */}
			{status === "rejected" && (
				<div className="confirmation-card-result confirmation-card-result--rejected">
					<DashIcon />
					<span className="confirmation-card-result-text">Action cancelled</span>
				</div>
			)}

			{/*
			  * Assertive live region — updated only when the card transitions to expired.
			  * Kept outside all conditional branches so it persists in the DOM.
			  * Screen readers will announce this immediately when the text changes.
			  */}
			<span
				className="confirmation-card-expiry-announcer"
				aria-live="assertive"
				aria-atomic="true"
				role="status"
			>
				{isExpired ? "Action proposal has expired and can no longer be approved." : ""}
			</span>
		</div>
	);
}
